import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { getSeatPoses } from './lib/seatLayout';

const IDLE_CUSHION = '#94A3B8';
const IDLE_BACK = '#64748B';
const SELECTED_CUSHION = '#F1F5F9';
const SELECTED_BACK = '#CBD5E1';

/**
 * Top-down chairs placed from the table's real width/height, mirroring the POS
 * client (features/dine-in/floor-plan/components/TableSeats.tsx).
 */
const TableSeats = ({ shape, width, height, capacity, selected = false }) => {
  const poses = useMemo(
    () => getSeatPoses(shape, width, height, capacity),
    [shape, width, height, capacity]
  );

  if (
    poses.length === 0 ||
    !Number.isFinite(width) ||
    !Number.isFinite(height)
  ) {
    return null;
  }

  const shortSide = Math.min(width, height);
  const density = Math.max(1, Math.min(capacity, 12) / 4);
  const damp = Math.min(1.25, 0.9 + density * 0.08);
  const seatW = Math.max(11, Math.min(18, shortSide * 0.14) / damp);
  const seatH = Math.max(10, Math.min(15, shortSide * 0.12) / damp);
  const backH = Math.max(2.5, seatH * 0.26);
  const cushionH = Math.max(4, seatH - backH - 1);
  const cushion = selected ? SELECTED_CUSHION : IDLE_CUSHION;
  const back = selected ? SELECTED_BACK : IDLE_BACK;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'visible',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {poses.map((pose, index) => {
        if (!Number.isFinite(pose.x) || !Number.isFinite(pose.y)) {
          return null;
        }
        return (
          <Box
            key={`seat-${index}`}
            sx={{
              position: 'absolute',
              left: pose.x - seatW / 2,
              top: pose.y - seatH / 2,
              width: seatW,
              height: seatH,
              transform: `rotate(${pose.rotation || 0}deg)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                width: '70%',
                alignSelf: 'center',
                height: backH,
                mb: '1px',
                bgcolor: back,
                borderRadius: `${backH / 2}px`,
              }}
            />
            <Box
              sx={{
                width: '100%',
                height: cushionH,
                bgcolor: cushion,
                borderRadius: `${Math.max(3, cushionH * 0.4)}px`,
                border: `1px solid ${
                  selected ? 'rgba(15,23,42,0.2)' : 'rgba(15,23,42,0.14)'
                }`,
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default React.memo(TableSeats);
